import clsx from "clsx"
import { useEffect, useRef } from "react"

import { useComponentNavigation } from "@organisms/ComponentNavigation/ComponentNavigation.context"

import styles from "./ComponentNavigationComponent.module.scss"
import { ComponentNavigationComponentProps } from "./ComponentNavigationComponent.types"

export default function ComponentNavigationComponent(props: ComponentNavigationComponentProps) {
    const { children, componentId, onEnter, onExit, className } = props
    const { setContentHeight, currentComponentId, componentIds, keepMounted, transitionDurationMS } =
        useComponentNavigation()

    const componentRef = useRef<HTMLDivElement>(null)
    const inertRef = useRef<HTMLDivElement>(null)
    const isActive = currentComponentId === componentId

    const wasActive = useRef(isActive)
    useEffect(() => {
        if (isActive) {
            onEnter?.()
        }
        if (!isActive && wasActive.current) {
            onExit?.()
        }
        wasActive.current = isActive
    }, [isActive])

    useEffect(() => {
        const element = componentRef.current

        if (keepMounted || !isActive || !element) {
            return
        }

        let rafId: number | null = null

        const measureHeight = () => {
            // Temporarily remove height constraint to measure natural content height
            element.setAttribute("data-measuring", "true")
            // Force reflow
            void element.offsetHeight
            const height = element.scrollHeight
            element.removeAttribute("data-measuring")
            setContentHeight(height)
        }

        const debouncedMeasureHeight = () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId)
            }
            rafId = requestAnimationFrame(measureHeight)
        }

        // Measure immediately
        measureHeight()

        // Observe element box size changes
        const resizeObserver = new ResizeObserver(measureHeight)
        resizeObserver.observe(element)

        // Also observe DOM changes (e.g., error messages appearing/disappearing) since
        // the element's box size is pinned by `inset: 0` and ResizeObserver won't fire
        // when only the content height changes.
        const mutationObserver = new MutationObserver(debouncedMeasureHeight)
        mutationObserver.observe(element, { childList: true, subtree: true })

        return () => {
            resizeObserver.unobserve(element)
            mutationObserver.disconnect()
            if (rafId !== null) {
                cancelAnimationFrame(rafId)
            }
        }
    }, [isActive, componentId, setContentHeight, keepMounted])

    // Imperatively set inert attribute to prevent keyboard focus on inactive panels
    useEffect(() => {
        if (!keepMounted || !inertRef.current) {
            return
        }
        if (isActive) {
            inertRef.current.removeAttribute("inert")
        } else {
            inertRef.current.setAttribute("inert", "")
        }
    }, [keepMounted, isActive])

    if (keepMounted) {
        const myIndex = componentIds.indexOf(componentId)
        const currentIndex = componentIds.indexOf(currentComponentId ?? "")
        const xOffset = myIndex < currentIndex ? "-100%" : myIndex > currentIndex ? "100%" : "0%"

        return (
            <div
                ref={inertRef}
                className={clsx(className, isActive ? styles.keepMountedActive : styles.keepMountedInactive)}
                style={{
                    transform: `translateX(${xOffset})`,
                    opacity: isActive ? 1 : 0,
                    transition: `transform ${transitionDurationMS}ms ease-in-out, opacity ${transitionDurationMS}ms ease-in-out`,
                }}
            >
                {children}
            </div>
        )
    }

    return (
        <div ref={componentRef} className={clsx(styles.base, className)}>
            {children}
        </div>
    )
}
